import { Component, computed, input, OnInit, output, signal, viewChild } from '@angular/core';
import { shuffle } from '@gametailors/web-core/misc';
import { IconEnter } from '../../../icons/i-enter';
import { RidBottomButton } from '../../rid-bottom-button/rid-bottom-button';
import { RidButton } from '../../rid-button/rid-button';
import { RidDraggable } from '../../rid-draggable/rid-draggable';
import { RidKlankCard } from '../../rid-klank-card/rid-klank-card';
import { RidLettermixWord } from '../../rid-lettermix-word/rid-lettermix-word';
import { RidMechanicBackground } from '../../rid-mechanic-background/rid-mechanic-background';
import { RidMechanicContent } from '../../rid-mechanic-content/rid-mechanic-content';
import { RidMechanicTitle } from '../../rid-mechanic-title/rid-mechanic-title';
import { RidMechanicWrapper } from '../../rid-mechanic-wrapper/rid-mechanic-wrapper';
import { RidOverlayFeedback } from '../../rid-overlay-feedback/rid-overlay-feedback';
import { RidSpacer } from '../../rid-spacer/rid-spacer';
import { RidTopBar } from '../../rid-top-bar/rid-top-bar';
import { RidWordBuildingSlot } from './rid-word-building-slot';

type WordTuple = [prefix: string | undefined, root: string, suffix: string | undefined];

@Component({
  selector: 'rid-word-building',
  imports: [
    RidMechanicBackground,
    RidMechanicWrapper,
    RidTopBar,
    RidMechanicContent,
    RidMechanicTitle,
    RidOverlayFeedback,
    RidButton,
    RidLettermixWord,
    RidBottomButton,
    RidKlankCard,
    RidDraggable,
    RidSpacer,
    RidWordBuildingSlot,
    IconEnter,
  ],
  templateUrl: './rid-word-building.html',
  host: { class: 'block' },
})
export class RidWordBuilding implements OnInit {
  assignments = input.required<{ root: string; words: WordTuple[] }[]>();

  index = signal(0);
  progress = signal(0);
  prefix = signal<string | undefined>(undefined);
  suffix = signal<string | undefined>(undefined);
  assignment = signal<{
    id: string;
    options: string[];
    root: string;
    answers: { word: WordTuple; display: string[]; appearance: 'REVEAL' | 'CORRECT' | 'SPOILER' }[];
  }>({ id: crypto.randomUUID(), options: [], root: '', answers: [] });

  canGoToNext = computed(() => this.assignment().answers.every((a) => a.appearance !== 'SPOILER'));
  hasInput = computed(() => this.prefix() !== undefined || this.suffix() !== undefined);
  longestOption = computed(() => {
    const options = this.assignment().options;
    return options.reduce((longest, o) => (o.length > longest.length ? o : longest), '');
  });
  currentWord = computed(() => [this.prefix(), this.assignment().root, this.suffix()].filter((p): p is string => p !== undefined).join(''));
  seenWords = computed(() =>
    this.assignment()
      .answers.filter((a) => a.appearance === 'CORRECT')
      .map((a) => a.display.join('')),
  );
  overlayFeedback = viewChild.required<RidOverlayFeedback>('overlayFeedback');
  prefixDropZone = viewChild.required<RidWordBuildingSlot>('prefixDropZone');
  suffixDropZone = viewChild.required<RidWordBuildingSlot>('suffixDropZone');

  finished = output<void>();

  ngOnInit(): void {
    this.initialize(this.assignments()[this.index()]);
  }

  initialize(data: { root: string; words: WordTuple[] }) {
    const allParts = data.words.flatMap((w) => [w[0], w[2]]).filter((p): p is string => p !== undefined);
    const options = shuffle(Array.from(new Set(allParts)));

    this.assignment.set({
      id: crypto.randomUUID(),
      options,
      root: data.root,
      answers: data.words.map((w) => ({
        word: w,
        display: [w[0], w[1], w[2]].filter((p): p is string => p !== undefined),
        appearance: 'SPOILER' as const,
      })),
    });
  }

  onDragMove(draggable: RidDraggable, point: { x: number; y: number }) {
    for (const zone of [this.prefixDropZone(), this.suffixDropZone()]) {
      zone.handleMove(draggable, point);
    }
  }

  onDragRelease(draggable: RidDraggable, optionIndex: number) {
    const klank = this.assignment().options[optionIndex];
    for (const [zone, slot] of [
      [this.prefixDropZone(), 'PREFIX'],
      [this.suffixDropZone(), 'SUFFIX'],
    ] as const) {
      if (zone.isSnapped(draggable)) {
        zone.unsnap(draggable);

        if (slot === 'PREFIX') this.prefix.set(klank);
        else this.suffix.set(klank);

        draggable.respawn();
        return;
      }
    }
  }

  clearSlot(slot: 'PREFIX' | 'SUFFIX') {
    if (slot === 'PREFIX') this.prefix.set(undefined);
    else this.suffix.set(undefined);
  }

  check() {
    const prefix = this.prefix() ?? undefined;
    const suffix = this.suffix() ?? undefined;
    const words = this.assignments()[this.index()].words;
    const answers = this.assignment().answers;

    const correctWordIndex = words.findIndex((w, i) => w[0] === prefix && w[2] === suffix && answers[i].appearance === 'SPOILER');

    if (correctWordIndex >= 0) {
      this.assignment.update((assignment) => {
        const newAssignment = { ...assignment };
        newAssignment.answers[correctWordIndex].appearance = 'CORRECT';
        return newAssignment;
      });
      this.prefix.set(undefined);
      this.suffix.set(undefined);
      this.overlayFeedback().showCorrect();
    } else {
      this.prefix.set(undefined);
      this.suffix.set(undefined);
      this.overlayFeedback().showIncorrect();
    }
  }

  next() {
    const nextIndex = this.index() + 1;
    if (nextIndex >= this.assignments().length) {
      this.finish();
    } else {
      this.index.set(nextIndex);
      this.prefix.set(undefined);
      this.suffix.set(undefined);
      this.initialize(this.assignments()[nextIndex]);
      this.progress.update((p) => p + 1);
    }
  }

  reveal() {
    this.prefix.set(undefined);
    this.suffix.set(undefined);
    this.assignment.update((assignment) => {
      const newAssignment = { ...assignment };
      newAssignment.answers = newAssignment.answers.map((a) => ({
        ...a,
        appearance: a.appearance === 'SPOILER' ? ('REVEAL' as const) : a.appearance,
      }));
      return newAssignment;
    });
  }

  finish() {
    this.finished.emit();
  }
}
